// pages/config/StreamSetup.jsx
// All overlay-related configuration in one place. Mounted as the "Stream
// Setup" tab in the Admin panel (HelpView). Reads + writes:
//   - config/stream          (titles, marquee, characters, break/outro, controls)
//   - config/alertTemplates  (per-type message templates)
//   - alerts                 (test alert writes from the Test Alerts harness)
//
// Layout: five tabs, grouped by what each option controls on the overlays.
//   🎬 Scenes       — per-scene Preview + Copy URL buttons, Event Theme
//   🖥 Screens      — per-scene settings (Starting Soon / QR Card / Break /
//                     Outro / Socials / Face Cam) as collapsible groups
//   📣 Messages     — marquee editor + booth showcase (rotating content)
//   🔔 Alerts       — live guardrail + mute, per-type template + test rows,
//                     free-form custom alert
//   🔗 Integrations — generic webhook, Twitch EventSub, Square tips
//
// On first save, marqueeMessages migrates from config/overlay → config/stream.
import { useEffect, useMemo, useRef, useState } from "react";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "./../../firebase";
import {
  ALERT_DEFAULTS,
  ALERT_LABELS,
  ALERT_TYPES,
  DEFAULT_ALERT_TEMPLATES,
  OVERLAY_SCENES,
  TEST_ALERT_SAMPLES,
  writeAlert,
} from "./../../utils/alerts";
import { CARD_LAYOUT_OPTIONS } from "./../../components/overlay/MarqueeCard";
import { OVERLAY_THEMES } from "./../../utils/overlayTheme";

// BRAND sourced from shared staff theme.
import { STAFF_THEME } from "../../theme/staff";
import { describeSaveError } from "../../utils/authDebug";
const BRAND = STAFF_THEME;

const DURATION_OPTS = [4, 6, 8, 10, 15, 20];

// Featured-character checkbox options. Keys reference flash design ids; the
// CharacterBadge / character names live in the alerts/util layer.
const FEATURED_CHOICES = [
  { id: "charli",         label: "Charli" },
  { id: "robert",         label: "Trav" },
  { id: "saturnina",      label: "Saturnina" },
  { id: "sunnySugar",     label: "Sunny & Sugar" },
  { id: "hermiethewurmy", label: "Hermie" },
  { id: "jacques",        label: "Jacques (coming soon)" },
  { id: "poffridge",      label: "Poffridge (coming soon)" },
];

// Page tabs. Grouped by what each option controls on the overlay scenes.
const TABS = [
  { id: "scenes",       label: "🎬 Scenes" },
  { id: "screens",      label: "🖥 Screens" },
  { id: "messages",     label: "📣 Messages" },
  { id: "alerts",       label: "🔔 Alerts" },
  { id: "integrations", label: "🔗 Integrations" },
];

const DEFAULT_MARQUEE = [
  { text: "Get your Golden Ticket ↑", enabled: true, durationSec: 6 },
  { text: "Happy Batterday! Follow @thejoyofpancakes 🥞", enabled: true, durationSec: 8 },
  { text: "We flip for tips 🫙", enabled: true, durationSec: 6 },
  { text: "Hire us for your next event ✨", enabled: true, durationSec: 6 },
  { text: "Preserve your pancake forever", enabled: true, durationSec: 6 },
];

const STREAM_DEFAULTS = {
  streamTitle: "Live Pancake Art",
  streamSubtitle: "Get your Golden Ticket",
  marqueeMessages: DEFAULT_MARQUEE,
  featuredCharacters: ["charli", "robert", "saturnina"],
  breakMessage: "Back in a few — don't lose your spot in the queue!",
  breakReturnTime: "10 minutes",
  outroMessage: "Thanks for watching! See you next time.",
  featuredSocials: { twitch: "twitch.tv/thejoyofpancakes", instagram: "@thejoyofpancakes", web: "thejoyofpancakes.com" },
  tipJarUrl: "https://thejoyofpancakes.com",
  queueVisibleOnStartingScreen: true,
  showJoinLinkOnStartingScreen: true,
  showQueueCounterOnFaceCam: true,
  alertSoundsMuted: false,
  streamState: "offline",
  overlayTheme: "classic",
  boothGalaMode: false,
};

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function StreamSetup() {
  const [config, setConfig]       = useState(null); // null = loading
  const [templates, setTemplates] = useState(null);
  const [legacyMarquee, setLegacyMarquee] = useState(null); // from old config/overlay
  const [saving, setSaving]       = useState(false);
  const [savedAt, setSavedAt]     = useState(null);
  const [testFiring, setTestFiring] = useState(null); // alert type currently firing
  const [tab, setTab] = useState("scenes");

  // Load config/stream
  useEffect(() => {
    return onSnapshot(doc(db, "config", "stream"), (snap) => {
      if (snap.exists()) setConfig({ ...STREAM_DEFAULTS, ...snap.data() });
      else setConfig({ ...STREAM_DEFAULTS });
    }, () => setConfig({ ...STREAM_DEFAULTS }));
  }, []);

  // Load legacy config/overlay marquee for one-time migration
  useEffect(() => {
    return onSnapshot(doc(db, "config", "overlay"), (snap) => {
      if (snap.exists() && Array.isArray(snap.data().marqueeMessages)) {
        setLegacyMarquee(snap.data().marqueeMessages);
      }
    }, () => {});
  }, []);

  // Load alert templates
  useEffect(() => {
    return onSnapshot(doc(db, "config", "alertTemplates"), (snap) => {
      if (snap.exists()) setTemplates({ ...DEFAULT_ALERT_TEMPLATES, ...snap.data() });
      else setTemplates({ ...DEFAULT_ALERT_TEMPLATES });
    }, () => setTemplates({ ...DEFAULT_ALERT_TEMPLATES }));
  }, []);

  // Migrate legacy marquee into in-memory config (visible to user, ready to
  // save). Runs once when config loads and config has no marqueeMessages yet
  // but legacyMarquee exists.
  useEffect(() => {
    if (!config || !legacyMarquee) return;
    const cur = Array.isArray(config.marqueeMessages) ? config.marqueeMessages : [];
    if (cur.length === 0 && legacyMarquee.length > 0) {
      setConfig((c) => ({ ...c, marqueeMessages: legacyMarquee }));
    }
  }, [config, legacyMarquee]); // eslint-disable-line react-hooks/exhaustive-deps

  function update(field, value) {
    setConfig((c) => ({ ...c, [field]: value }));
  }
  function updateNested(parent, field, value) {
    setConfig((c) => ({ ...c, [parent]: { ...(c[parent] || {}), [field]: value } }));
  }
  function updateTemplate(type, value) {
    setTemplates((t) => ({ ...t, [type]: value }));
  }

  async function saveAll() {
    setSaving(true);
    try {
      await setDoc(doc(db, "config", "stream"), config, { merge: true });
      await setDoc(doc(db, "config", "alertTemplates"), templates, { merge: true });
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2400);
    } catch (err) {
      alert("Save failed: " + describeSaveError(err));
    }
    setSaving(false);
  }

  async function fireTestAlert(type) {
    // Live-stream guardrail — fired alerts render to real viewers on the
    // /overlay/alerts browser source. Per CLAUDE.md, "Mistakes ship to
    // customers." Require explicit confirmation when streamState is "live".
    if (config?.streamState === "live") {
      const ok = window.confirm(
        "⚠️ Stream is LIVE — this test alert will render to real viewers in OBS. Fire anyway?"
      );
      if (!ok) return;
    }
    setTestFiring(type);
    try {
      await writeAlert(type, TEST_ALERT_SAMPLES[type] || {});
    } catch (err) {
      alert("Test alert failed: " + err.message);
    }
    setTimeout(() => setTestFiring(null), 800);
  }

  if (!config || !templates) {
    return <div style={{ color: BRAND.muted, fontSize: "0.85rem", padding: "2rem", textAlign: "center" }}>Loading stream setup…</div>;
  }

  return (
    <div style={{ padding: "1rem 1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>

      {/* Sticky header — tab bar + save */}
      <div style={{
        position: "sticky", top: 0, zIndex: 5,
        background: BRAND.dark, padding: "0.5rem 0 0.6rem",
        marginTop: "-1rem", marginBottom: "0.25rem",
        display: "flex", flexDirection: "column", gap: "0.5rem",
      }}>
        <div style={{ display: "flex", gap: "0.35rem", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                style={{
                  flexShrink: 0,
                  background: active ? "rgba(252,179,31,0.14)" : BRAND.raised,
                  border: `1px solid ${active ? BRAND.gold : BRAND.border}`,
                  borderRadius: "999px",
                  color: active ? BRAND.gold : BRAND.muted,
                  fontSize: "0.82rem", fontWeight: 700,
                  padding: "0.45rem 0.85rem", cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >{t.label}</button>
            );
          })}
        </div>
        {tab !== "integrations" && (
          <button
            onClick={saveAll}
            disabled={saving}
            style={{
              background: savedAt ? BRAND.green : BRAND.gold,
              color: savedAt ? "white" : BRAND.dark,
              border: "none", borderRadius: "10px",
              fontSize: "0.95rem", fontWeight: "bold",
              padding: "0.7rem", cursor: "pointer",
            }}
          >
            {saving ? "Saving…" : savedAt ? "✓ Saved!" : "💾 Save Stream Config"}
          </button>
        )}
      </div>

      {/* ══ 🎬 SCENES — OBS sources + event theme ═══════════════════════════ */}
      {tab === "scenes" && (<>
      <Section title="🎬 Overlay Scenes" subtitle="Click 👁 to preview a scene in a popup window. Copy the URL into OBS as a Browser Source.">
        <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
          {OVERLAY_SCENES.map((s) => (
            <SceneRow key={s.id} scene={s} />
          ))}
        </div>
      </Section>

      {/* Event theme */}
      <Section
        title="🎨 Event Theme"
        subtitle="Retints every overlay scene live — colors only, layouts untouched. Preview any scene without saving by adding ?theme=zoofari to its URL."
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
          {Object.values(OVERLAY_THEMES).map((t) => {
            const active = (config.overlayTheme || "classic") === t.id;
            const swatches = t.id === "classic"
              ? ["#fcb31f", "#1f59db", "#4aa4e1", "#ef537e", "#ffdaad"]
              : [t.palette.gold, t.palette.blue, "#4aa4e1", t.palette.pink, t.palette.cream].filter(Boolean);
            return (
              <button
                key={t.id}
                onClick={() => update("overlayTheme", t.id)}
                style={{
                  display: "flex", alignItems: "center", gap: "0.7rem",
                  background: active ? "rgba(252,179,31,0.12)" : "rgba(255,255,255,0.04)",
                  border: active ? `2px solid ${BRAND.gold}` : `1px solid ${BRAND.border}`,
                  borderRadius: "10px", padding: "0.65rem 0.8rem",
                  cursor: "pointer", textAlign: "left",
                }}
              >
                <span style={{ fontSize: "1rem" }}>{active ? "✓" : ""}</span>
                <span style={{ color: "white", fontWeight: 600, fontSize: "0.92rem", flex: 1 }}>{t.label}</span>
                <span style={{ display: "flex", gap: "4px" }}>
                  {swatches.map((c, i) => (
                    <span key={i} style={{ width: 16, height: 16, borderRadius: "50%", background: c, border: "1px solid rgba(255,255,255,0.25)" }} />
                  ))}
                </span>
              </button>
            );
          })}
        </div>
      </Section>

      {/* Gala mode — Booth display bottom rail */}
      <Section
        title="🥂 Gala Mode (Booth display)"
        subtitle="For premade / grab-and-go events with no live queue: swaps the Booth display's bottom pancake tracker for three booking-pitch cards (gluten-free poffertjes & catering · get-a-quote QR · live pancake art). Toggle off to restore the normal tracker. Takes effect live — no OBS refresh needed. Heads up: Event Config profiles stamp this on Apply, so applying a profile overrides whatever is set here."
      >
        <ToggleRow
          label="🥂 Gala mode — booking pitch instead of the live tracker"
          checked={!!config.boothGalaMode}
          onChange={(v) => update("boothGalaMode", v)}
        />
      </Section>

      </>)}

      {/* ══ 🖥 SCREENS — per-scene settings ══════════════════════════════════ */}
      {tab === "screens" && (<>
      <Collapsible title="🌅 Starting Soon" subtitle="Pre-stream title card — headline, characters, queue list." defaultOpen>
        <Field label="Stream title">
          <input style={inputStyle} value={config.streamTitle ?? ""} onChange={(e) => update("streamTitle", e.target.value)} />
        </Field>
        <Field label="Stream subtitle">
          <input style={inputStyle} value={config.streamSubtitle ?? ""} onChange={(e) => update("streamSubtitle", e.target.value)} />
        </Field>
        <Field label="Featured characters" hint="Rotating character badges on Starting Soon. Also the character list for custom alerts.">
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {FEATURED_CHOICES.map((c) => {
              const checked = (config.featuredCharacters || []).includes(c.id);
              return (
                <label key={c.id} style={{
                  display: "flex", alignItems: "center", gap: "0.35rem",
                  background: checked ? "rgba(74,164,225,0.18)" : BRAND.raised,
                  border: `1px solid ${checked ? BRAND.sky : BRAND.border}`,
                  borderRadius: "999px", padding: "0.3rem 0.7rem",
                  cursor: "pointer", fontSize: "0.78rem",
                  color: checked ? BRAND.sky : BRAND.muted,
                }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const cur = config.featuredCharacters || [];
                      const next = e.target.checked ? [...cur, c.id] : cur.filter((x) => x !== c.id);
                      update("featuredCharacters", next);
                    }}
                    style={{ accentColor: BRAND.sky, cursor: "pointer" }}
                  />
                  {c.label}
                </label>
              );
            })}
          </div>
        </Field>
        <ToggleRow
          label="Show live queue list"
          checked={!!config.queueVisibleOnStartingScreen}
          onChange={(v) => update("queueVisibleOnStartingScreen", v)}
        />
      </Collapsible>

      <Collapsible title="🎟 QR Card" subtitle="QR codes on the Starting Soon scene and the Booth display.">
        <ToggleRow
          label="Show QR card on Starting Soon (only appears when a custom QR URL is set below)"
          checked={!!config.showJoinLinkOnStartingScreen}
          onChange={(v) => update("showJoinLinkOnStartingScreen", v)}
        />
        <ToggleRow
          label="Show order QR on Booth display (auto-hides while the queue is closed)"
          checked={!!config.showQrOnBooth}
          onChange={(v) => update("showQrOnBooth", v)}
        />
        <Field label="QR URL" hint="Booth falls back to the default /join link. The Starting Soon card only shows when a URL is entered here — the queue isn't open pre-show, so there's no default.">
          <input style={inputStyle} value={config.customQrUrl ?? ""} onChange={(e) => update("customQrUrl", e.target.value)} placeholder="https://..." />
        </Field>
        <Field label="QR card headline" hint="Leave blank for default '📱 Scan for more'">
          <input style={inputStyle} value={config.customQrLabel ?? ""} onChange={(e) => update("customQrLabel", e.target.value)} placeholder="📱 Scan for more" />
        </Field>
        <Field label="QR card subtext" hint="Leave blank for default 'Point your camera at the code'. Starting Soon only.">
          <input style={inputStyle} value={config.customQrSublabel ?? ""} onChange={(e) => update("customQrSublabel", e.target.value)} placeholder="Point your camera at the code" />
        </Field>
      </Collapsible>

      <Collapsible title="☕ Break" subtitle="The 'be right back' badge on the Break scene.">
        <Field label="Break message">
          <input style={inputStyle} value={config.breakMessage ?? ""} onChange={(e) => update("breakMessage", e.target.value)} />
        </Field>
        <Field label="Estimated return time" hint="Free text — '10 minutes', 'soon!'. Shows under the break message; leave blank to hide.">
          <input style={inputStyle} value={config.breakReturnTime ?? ""} onChange={(e) => update("breakReturnTime", e.target.value)} />
        </Field>
      </Collapsible>

      <Collapsible title="👋 Outro" subtitle="End-of-stream credits — sign-off, tip jar, pancake gallery.">
        <Field label="Sign-off message">
          <input style={inputStyle} value={config.outroMessage ?? ""} onChange={(e) => update("outroMessage", e.target.value)} />
        </Field>
        <Field label="Tip jar URL">
          <input style={inputStyle} value={config.tipJarUrl ?? ""} onChange={(e) => update("tipJarUrl", e.target.value)} />
        </Field>
      </Collapsible>

      <Collapsible title="📱 Socials" subtitle="Handles shown on the Outro and Screen Share scenes.">
        <Field label="Twitch">
          <input style={inputStyle} value={config.featuredSocials?.twitch ?? ""} onChange={(e) => updateNested("featuredSocials", "twitch", e.target.value)} />
        </Field>
        <Field label="Instagram">
          <input style={inputStyle} value={config.featuredSocials?.instagram ?? ""} onChange={(e) => updateNested("featuredSocials", "instagram", e.target.value)} />
        </Field>
        <Field label="Web">
          <input style={inputStyle} value={config.featuredSocials?.web ?? ""} onChange={(e) => updateNested("featuredSocials", "web", e.target.value)} />
        </Field>
      </Collapsible>

      <Collapsible title="🎥 Face Cam" subtitle="Camera frame scene.">
        <ToggleRow
          label="Show '🎟 N waiting' queue counter"
          checked={!!config.showQueueCounterOnFaceCam}
          onChange={(v) => update("showQueueCounterOnFaceCam", v)}
        />
      </Collapsible>
      </>)}

      {/* ══ 📣 MESSAGES — rotating content ═══════════════════════════════════ */}
      {tab === "messages" && (<>
      <Section title="📣 Marquee Messages" subtitle="Scrolling ticker on the Break + Starting Soon scenes. Trailing emojis get a wiggle animation. Stream/Booth context picks which displays show each message.">
        <MarqueeEditor
          messages={config.marqueeMessages || []}
          onChange={(next) => update("marqueeMessages", next)}
        />
      </Section>

      <BoothShowcaseSection />
      </>)}

      {/* ══ 🔔 ALERTS ════════════════════════════════════════════════════════ */}
      {tab === "alerts" && (<>
      <Section title="🎛 Alert Controls">
        <ToggleRow
          label="🔴 Stream is LIVE — ask before firing test alerts to real viewers"
          checked={config.streamState === "live"}
          onChange={(v) => update("streamState", v ? "live" : "offline")}
        />
        <ToggleRow
          label="🔇 Mute alert sounds"
          checked={!!config.alertSoundsMuted}
          onChange={(v) => update("alertSoundsMuted", v)}
        />
      </Section>

      <Section
        title="🔔 Alert Types"
        subtitle="Edit each alert's on-screen message — variables: {name}, {subject}, {count}, {message}. 🧪 fires a test alert with sample data to /overlay/alerts (open a preview from the Scenes tab to watch)."
      >
        {ALERT_TYPES.map((type) => (
          <AlertTypeRow
            key={type}
            type={type}
            value={templates[type] ?? ""}
            onChange={(v) => updateTemplate(type, v)}
            onFire={() => fireTestAlert(type)}
            firing={testFiring === type}
          />
        ))}
      </Section>

      <CustomAlertSection />
      </>)}

      {/* ══ 🔗 INTEGRATIONS ══════════════════════════════════════════════════ */}
      {tab === "integrations" && (<>
      <WebhookSection />
      <TwitchSection />
      <SquareWebhookSection />
      </>)}

      <div style={{ height: "2rem" }} />
    </div>
  );
}

// ── SECTION WRAPPER ──────────────────────────────────────────────────────────
function Section({ title, subtitle, children }) {
  return (
    <div style={{
      background: BRAND.card,
      borderRadius: "12px",
      padding: "1.1rem",
      display: "flex",
      flexDirection: "column",
      gap: "0.75rem",
      border: `1px solid ${BRAND.border}`,
    }}>
      <div>
        <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "0.95rem" }}>{title}</div>
        {subtitle && (
          <div style={{ color: BRAND.muted, fontSize: "0.78rem", lineHeight: 1.5, marginTop: "2px" }}>{subtitle}</div>
        )}
      </div>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
      <label style={{ color: BRAND.dim, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</label>
      {children}
      {hint && <div style={{ color: BRAND.dim, fontSize: "0.7rem", lineHeight: 1.4 }}>{hint}</div>}
    </div>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <label style={{
      display: "flex", alignItems: "center", gap: "0.6rem",
      background: BRAND.raised, border: `1px solid ${checked ? BRAND.sky : BRAND.border}`,
      borderRadius: "10px", padding: "0.6rem 0.9rem",
      cursor: "pointer", userSelect: "none",
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: BRAND.sky, cursor: "pointer" }}
      />
      <div style={{ color: checked ? BRAND.sky : "white", fontSize: "0.85rem", fontWeight: 600 }}>{label}</div>
    </label>
  );
}

// Collapsible card — same look as Section, but the header toggles the body.
// Used for per-scene groups (Screens tab) and the integration reference cards.
function Collapsible({ title, subtitle, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      background: BRAND.card,
      borderRadius: "12px",
      border: `1px solid ${BRAND.border}`,
    }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", background: "none", border: "none", cursor: "pointer",
          padding: "1rem 1.1rem", textAlign: "left",
          display: "flex", alignItems: "flex-start", gap: "0.6rem",
        }}
      >
        <span style={{
          color: BRAND.gold, fontSize: "0.75rem", marginTop: "3px",
          display: "inline-block",
          transform: open ? "rotate(90deg)" : "none",
          transition: "transform 0.15s ease",
        }}>▶</span>
        <span style={{ flex: 1 }}>
          <span style={{ display: "block", color: BRAND.gold, fontWeight: "bold", fontSize: "0.95rem" }}>{title}</span>
          {subtitle && (
            <span style={{ display: "block", color: BRAND.muted, fontSize: "0.78rem", lineHeight: 1.5, marginTop: "2px" }}>{subtitle}</span>
          )}
        </span>
      </button>
      {open && (
        <div style={{ padding: "0 1.1rem 1.1rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {children}
        </div>
      )}
    </div>
  );
}

// One row per alert type — message template + test-fire button in one place
// (replaces the old separate Alert Templates and Test Alerts sections).
function AlertTypeRow({ type, value, onChange, onFire, firing }) {
  const def = ALERT_DEFAULTS[type] || {};
  return (
    <div style={{
      background: BRAND.raised, border: `1px solid ${BRAND.border}`,
      borderRadius: "10px", padding: "0.6rem 0.7rem",
      display: "flex", flexDirection: "column", gap: "0.4rem",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ color: "white", fontSize: "0.84rem", fontWeight: 600 }}>{ALERT_LABELS[type] || type}</span>
          <span style={{ color: BRAND.dim, fontSize: "0.7rem", marginLeft: "0.5rem" }}>{def.character} · {def.position}</span>
        </div>
        <button
          onClick={onFire}
          disabled={firing}
          style={{
            ...miniBtn,
            background: firing ? BRAND.green : "none",
            borderColor: firing ? BRAND.green : BRAND.border,
            color: firing ? "white" : BRAND.sky,
            fontSize: "0.76rem", padding: "0.3rem 0.6rem",
          }}
        >{firing ? "✓ Fired" : "🧪 Test"}</button>
      </div>
      <input
        style={{ ...inputStyle, background: BRAND.panel }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={DEFAULT_ALERT_TEMPLATES[type]}
      />
    </div>
  );
}

const inputStyle = {
  width: "100%", boxSizing: "border-box",
  background: BRAND.raised, border: `1px solid ${BRAND.border}`, borderRadius: "8px",
  color: "white", fontSize: "0.85rem", padding: "0.5rem 0.7rem",
  outline: "none",
};

const miniBtn = {
  background: "none", border: `1px solid ${BRAND.border}`, borderRadius: "5px",
  color: "rgba(255,255,255,0.5)", fontSize: "0.72rem", padding: "0.15rem 0.4rem",
  cursor: "pointer", lineHeight: 1,
};

// ── SCENE ROW ────────────────────────────────────────────────────────────────
function SceneRow({ scene }) {
  const [copied, setCopied] = useState(false);
  const fullUrl = useMemo(() => `${window.location.origin}${scene.path}`, [scene.path]);

  function preview(orientation) {
    const w = orientation === "portrait" ? 540 : 960;
    const h = orientation === "portrait" ? 960 : 540;
    const features = `width=${w},height=${h},left=${Math.max(0, (window.screen.availWidth - w) / 2)},top=${Math.max(0, (window.screen.availHeight - h) / 2)},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=no`;
    // Append ?debug to the alerts scene so the corner HUD shows auth/listener
    // status — makes it obvious when the listener isn't picking up new alerts.
    const url = scene.id === "alerts" ? `${scene.path}?debug` : scene.path;
    window.open(url, `gts_preview_${scene.id}_${orientation}`, features);
  }
  function copy() {
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  return (
    <div style={{
      background: BRAND.raised, border: `1px solid ${scene.built ? BRAND.border : "rgba(255,255,255,0.06)"}`,
      borderRadius: "10px", padding: "0.7rem 0.85rem",
      opacity: scene.built ? 1 : 0.6,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: "140px" }}>
          <div style={{ color: "white", fontSize: "0.88rem", fontWeight: 600 }}>
            {scene.label}
            {!scene.built && <span style={{ color: BRAND.dim, fontWeight: "normal", marginLeft: "0.5rem", fontSize: "0.72rem" }}>(coming soon)</span>}
          </div>
          <div style={{ color: BRAND.sky, fontSize: "0.7rem", fontFamily: "monospace", marginTop: "1px" }}>{scene.path}</div>
        </div>
        <div style={{ display: "flex", gap: "0.3rem" }}>
          <button
            onClick={() => preview("landscape")}
            disabled={!scene.built}
            style={{ ...miniBtn, color: scene.built ? BRAND.sky : BRAND.dim, fontSize: "0.78rem", padding: "0.35rem 0.6rem", cursor: scene.built ? "pointer" : "not-allowed" }}
            title="Preview at 960×540 (landscape)"
          >👁 Landscape</button>
          <button
            onClick={() => preview("portrait")}
            disabled={!scene.built}
            style={{ ...miniBtn, color: scene.built ? BRAND.sky : BRAND.dim, fontSize: "0.78rem", padding: "0.35rem 0.6rem", cursor: scene.built ? "pointer" : "not-allowed" }}
            title="Preview at 540×960 (portrait)"
          >👁 Portrait</button>
          <button
            onClick={copy}
            style={{ ...miniBtn, color: copied ? BRAND.green : BRAND.muted, fontSize: "0.78rem", padding: "0.35rem 0.6rem" }}
          >{copied ? "✓ Copied" : "Copy URL"}</button>
        </div>
      </div>
      {scene.description && (
        <div style={{ color: BRAND.dim, fontSize: "0.72rem", marginTop: "0.3rem", lineHeight: 1.4 }}>{scene.description}</div>
      )}
    </div>
  );
}

// ── MARQUEE EDITOR ───────────────────────────────────────────────────────────
// ── MARQUEE EDITOR ──────────────────────────────────────────────────────────
// Each message carries:
//   text, enabled, durationSec   — original fields
//   context: "stream"|"booth"|"both"   — which overlay context shows it
//   type:    "text"|"card"             — text scrolls; card holds the slot
//   card:    { layout, title?, body?, items?[], imageUrl? }   — when type=card
// Missing context defaults to "both"; missing type defaults to "text" — so
// pre-existing messages keep playing in every mode until edited.

const CONTEXT_OPTS = [
  { id: "both",   label: "Both",   color: "#9da4b3" },
  { id: "stream", label: "Stream", color: "#4aa4e1" },
  { id: "booth",  label: "Booth",  color: "#fcb31f" },
];

// Initial booth-context message kit. Idempotent: the seed button only appends
// messages whose text/title isn't already present.
const BOOTH_SEEDS = [
  {
    type: "text", context: "booth", enabled: true, durationSec: 8,
    text: "Gluten-Free Poffertjes — Dutch mini-pancakes, made fresh.",
  },
  {
    type: "text", context: "booth", enabled: true, durationSec: 7,
    text: "What's a Golden Ticket? Ask Wella to find out.",
  },
  {
    type: "text", context: "booth", enabled: true, durationSec: 8,
    text: "Every pancake gets a keepsake page — scan your ticket after.",
  },
  {
    type: "card", context: "booth", enabled: true, durationSec: 12,
    card: { layout: "credentials", title: "AS SEEN ON" },
  },
  {
    type: "card", context: "booth", enabled: true, durationSec: 14,
    card: {
      layout: "menu",
      title: "TODAY AT THE BOOTH",
      items: [
        { label: "Flash Pancake Art", price: "$10" },
        { label: "Custom Pancake Art", price: "$30" },
        { label: "Gluten-Free Poffertjes", price: "$—", note: "ask for today's price" },
      ],
    },
  },
];

function seedKey(m) {
  if (m?.type === "card") return `card:${m.card?.layout}:${(m.card?.title || "").trim().toLowerCase()}`;
  return `text:${(m?.text || "").trim().toLowerCase()}`;
}

function itemsToText(items) {
  if (!Array.isArray(items)) return "";
  return items.map((it) => {
    const parts = [it.label || "", it.price || ""];
    if (it.note) parts.push(it.note);
    return parts.join(" | ");
  }).join("\n");
}

function textToItems(text) {
  return (text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [label = "", price = "", note = ""] = line.split("|").map((s) => s.trim());
      const item = { label };
      if (price) item.price = price;
      if (note)  item.note  = note;
      return item;
    });
}

function MarqueeEditor({ messages, onChange }) {
  function updateMsg(idx, patch) {
    onChange(messages.map((m, i) => i === idx ? { ...m, ...patch } : m));
  }
  function updateCard(idx, cardPatch) {
    onChange(messages.map((m, i) =>
      i === idx ? { ...m, card: { ...(m.card || {}), ...cardPatch } } : m
    ));
  }
  function removeMsg(idx) {
    onChange(messages.filter((_, i) => i !== idx));
  }
  function addText() {
    onChange([...messages, { text: "", enabled: true, durationSec: 6, type: "text", context: "both" }]);
  }
  function addCard() {
    onChange([...messages, {
      text: "", enabled: true, durationSec: 10,
      type: "card", context: "booth",
      card: { layout: "feature", title: "", body: "" },
    }]);
  }
  function seedBoothMessages() {
    const existing = new Set((messages || []).map(seedKey));
    const additions = BOOTH_SEEDS.filter((m) => !existing.has(seedKey(m)));
    if (additions.length === 0) return;
    onChange([...messages, ...additions]);
  }
  const boothSeedsAlreadyPresent = (() => {
    const existing = new Set((messages || []).map(seedKey));
    return BOOTH_SEEDS.every((m) => existing.has(seedKey(m)));
  })();
  function moveMsg(idx, dir) {
    const target = idx + dir;
    if (target < 0 || target >= messages.length) return;
    const arr = [...messages];
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    onChange(arr);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      {messages.map((msg, i) => {
        const ctx = msg.context || "both";
        const type = msg.type || "text";
        const isCard = type === "card";
        const layout = msg.card?.layout || "feature";
        const ctxColor = CONTEXT_OPTS.find((o) => o.id === ctx)?.color || "#9da4b3";
        return (
          <div key={i} style={{
            background: BRAND.raised, borderRadius: "8px", padding: "0.65rem",
            display: "flex", flexDirection: "column", gap: "0.4rem",
            border: `1px solid ${msg.enabled ? `${ctxColor}55` : "rgba(255,255,255,0.08)"}`,
            opacity: msg.enabled ? 1 : 0.55,
            borderLeft: `3px solid ${ctxColor}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <input
                type="checkbox"
                checked={msg.enabled}
                onChange={(e) => updateMsg(i, { enabled: e.target.checked })}
                style={{ width: "18px", height: "18px", flexShrink: 0, cursor: "pointer", accentColor: BRAND.sky }}
              />
              {!isCard ? (
                <input
                  type="text"
                  value={msg.text || ""}
                  onChange={(e) => updateMsg(i, { text: e.target.value })}
                  placeholder="Message text…"
                  style={{ flex: 1, background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "6px", color: "white", fontSize: "0.82rem", padding: "0.4rem 0.55rem", outline: "none", minWidth: 0 }}
                />
              ) : (
                <div style={{
                  flex: 1, color: BRAND.gold, fontSize: "0.82rem", fontWeight: 700,
                  letterSpacing: "0.05em",
                }}>
                  CARD · {(CARD_LAYOUT_OPTIONS.find((o) => o.id === layout)?.label || layout).toUpperCase()}
                </div>
              )}
            </div>

            {isCard ? (
              <div style={{
                display: "flex", flexDirection: "column", gap: "0.4rem",
                paddingLeft: "26px", paddingTop: "0.2rem",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
                  <label style={cardLabelStyle}>Layout</label>
                  <select
                    value={layout}
                    onChange={(e) => updateCard(i, { layout: e.target.value })}
                    style={cardSelectStyle}
                  >
                    {CARD_LAYOUT_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <input
                  type="text"
                  value={msg.card?.title || ""}
                  onChange={(e) => updateCard(i, { title: e.target.value })}
                  placeholder={
                    layout === "credentials" ? "Title (default: AS SEEN ON)" :
                    layout === "menu"        ? "Title (default: TODAY AT THE BOOTH)" :
                    "Title"
                  }
                  style={cardInputStyle}
                />
                {layout === "feature" ? (
                  <>
                    <textarea
                      value={msg.card?.body || ""}
                      onChange={(e) => updateCard(i, { body: e.target.value })}
                      placeholder="Body copy (one or two short lines)"
                      rows={2}
                      style={{ ...cardInputStyle, resize: "vertical", minHeight: "44px" }}
                    />
                    <input
                      type="text"
                      value={msg.card?.imageUrl || ""}
                      onChange={(e) => updateCard(i, { imageUrl: e.target.value })}
                      placeholder="Image URL (optional)"
                      style={cardInputStyle}
                    />
                  </>
                ) : null}
                {layout === "menu" ? (
                  <textarea
                    value={itemsToText(msg.card?.items)}
                    onChange={(e) => updateCard(i, { items: textToItems(e.target.value) })}
                    placeholder={"Flash Pancake Art | $10\nCustom Pancake Art | $30\nGluten-Free Poffertjes | $—"}
                    rows={4}
                    style={{ ...cardInputStyle, resize: "vertical", minHeight: "78px", fontFamily: "ui-monospace, monospace" }}
                  />
                ) : null}
                {layout === "credentials" ? (
                  <div style={{ color: BRAND.dim, fontSize: "0.72rem", lineHeight: 1.4 }}>
                    Press logos render from the booth-credentials list (Today, GMA,
                    Hell's Kitchen, Disney Parks, Kids' Choice). Edit the title to
                    change the lead-in copy.
                  </div>
                ) : null}
              </div>
            ) : null}

            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", paddingLeft: "26px" }}>
              <select
                value={ctx}
                onChange={(e) => updateMsg(i, { context: e.target.value })}
                title="Which overlay context shows this message"
                style={{ ...cardSelectStyle, color: ctxColor, borderColor: `${ctxColor}66` }}
              >
                {CONTEXT_OPTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
              <select
                value={type}
                onChange={(e) => {
                  const next = e.target.value;
                  if (next === "card" && !msg.card) {
                    updateMsg(i, { type: next, card: { layout: "feature" }, durationSec: Math.max(msg.durationSec || 6, 10) });
                  } else {
                    updateMsg(i, { type: next });
                  }
                }}
                title="Text scrolls; card holds the slot"
                style={cardSelectStyle}
              >
                <option value="text">Text</option>
                <option value="card">Card</option>
              </select>
              <select
                value={msg.durationSec || (isCard ? 10 : 6)}
                onChange={(e) => updateMsg(i, { durationSec: Number(e.target.value) })}
                style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "5px", color: BRAND.sky, fontSize: "0.72rem", padding: "0.25rem 0.35rem", cursor: "pointer" }}
              >
                {DURATION_OPTS.map((d) => <option key={d} value={d}>{d}s</option>)}
              </select>
              <div style={{ flex: 1 }} />
              <button onClick={() => moveMsg(i, -1)} disabled={i === 0} style={{ ...miniBtn, opacity: i === 0 ? 0.3 : 1 }}>↑</button>
              <button onClick={() => moveMsg(i, 1)} disabled={i === messages.length - 1} style={{ ...miniBtn, opacity: i === messages.length - 1 ? 0.3 : 1 }}>↓</button>
              <button onClick={() => removeMsg(i)} style={{ ...miniBtn, color: BRAND.pink }}>✕</button>
            </div>
          </div>
        );
      })}
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button
          onClick={addText}
          style={{
            flex: 1,
            background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "8px",
            color: BRAND.sky, fontSize: "0.82rem", padding: "0.55rem", cursor: "pointer", fontWeight: 600,
          }}
        >+ Add message</button>
        <button
          onClick={addCard}
          style={{
            flex: 1,
            background: BRAND.panel, border: `1px solid ${BRAND.gold}66`, borderRadius: "8px",
            color: BRAND.gold, fontSize: "0.82rem", padding: "0.55rem", cursor: "pointer", fontWeight: 600,
          }}
        >+ Add card (booth)</button>
      </div>
      {!boothSeedsAlreadyPresent ? (
        <button
          onClick={seedBoothMessages}
          title="Append the 5 booth-context starter messages (3 text + credentials card + menu card)"
          style={{
            background: "rgba(252,179,31,0.08)",
            border: `1px dashed ${BRAND.gold}66`,
            borderRadius: "8px",
            color: BRAND.gold, fontSize: "0.78rem",
            padding: "0.5rem", cursor: "pointer", fontWeight: 600,
          }}
        >🏪 Seed booth starter messages</button>
      ) : null}
    </div>
  );
}

const cardLabelStyle = {
  color: "rgba(255,255,255,0.55)",
  fontSize: "0.7rem",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
};
const cardInputStyle = {
  width: "100%",
  boxSizing: "border-box",
  background: BRAND.panel,
  border: `1px solid ${BRAND.border}`,
  borderRadius: "6px",
  color: "white",
  fontSize: "0.82rem",
  padding: "0.4rem 0.55rem",
  outline: "none",
};
const cardSelectStyle = {
  background: BRAND.panel,
  border: `1px solid ${BRAND.border}`,
  borderRadius: "5px",
  color: "white",
  fontSize: "0.72rem",
  padding: "0.25rem 0.35rem",
  cursor: "pointer",
};

// ── BOOTH SHOWCASE ──────────────────────────────────────────────────────────
// Editor for config/boothShowcase, consumed by /overlay/booth. Curated
// promotional images (poffertjes, fairy gardens, group pours, event photos)
// plus rotation flags for including the press-logos card and recent finished
// pancakes from the active shift.
function BoothShowcaseSection() {
  const [docState, setDocState] = useState(null); // null = loading
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    return onSnapshot(
      doc(db, "config", "boothShowcase"),
      (snap) => {
        if (snap.exists()) setDocState(snap.data());
        else setDocState({ items: [], rotation: {} });
      },
      () => setDocState({ items: [], rotation: {} })
    );
  }, []);

  if (!docState) {
    return (
      <Section title="🏪 Booth Showcase" subtitle="Loading…">
        <div style={{ color: BRAND.dim, fontSize: "0.82rem" }}>…</div>
      </Section>
    );
  }

  const items = Array.isArray(docState.items) ? docState.items : [];
  const rotation = {
    intervalSec: 12,           // default duration when an entry has no explicit override
    pressDurationSec: null,    // null = use intervalSec
    pancakeDurationSec: null,  // null = use intervalSec
    includePressCard: true,
    includeRecentPancakes: false,  // default off — perf issue with full-res cutouts
    recentPancakeCount: 3,
    ...(docState.rotation || {}),
  };

  function update(next) {
    setDocState((d) => ({ ...d, ...next }));
  }
  function updateRotation(field, value) {
    setDocState((d) => ({ ...d, rotation: { ...rotation, [field]: value } }));
  }
  function updateItem(idx, patch) {
    update({ items: items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) });
  }
  function removeItem(idx) {
    update({ items: items.filter((_, i) => i !== idx) });
  }
  function moveItem(idx, dir) {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const arr = [...items];
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    update({ items: arr });
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["png", "jpg", "jpeg", "webp", "gif"].includes(ext)) {
      alert("Supported formats: PNG, JPG, WebP, GIF");
      return;
    }
    setUploading(true);
    try {
      const cleanName = file.name.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9_-]/g, "_").toLowerCase();
      const path = `booth-showcase/${cleanName}_${Date.now()}.${ext}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, file, { contentType: file.type });
      const imageUrl = await getDownloadURL(ref);
      const id = `bs_${Date.now()}`;
      const label = file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ");
      update({
        items: [
          ...items,
          { id, imageUrl, caption: label, category: "", active: true, addedAt: Date.now() },
        ],
      });
    } catch (err) {
      alert("Upload failed: " + err.message);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function save() {
    setSaving(true);
    try {
      await setDoc(doc(db, "config", "boothShowcase"), docState, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert("Save failed: " + describeSaveError(err));
    }
    setSaving(false);
  }

  return (
    <Section
      title="🏪 Booth Showcase"
      subtitle="Rotating promo cards shown on the right column of /overlay/booth. Each card has a big promotional headline above the photo. Toggle whether press logos and recent pancakes also weave into the rotation."
    >
      {items.length === 0 ? (
        <div style={{
          color: BRAND.dim, fontSize: "0.82rem",
          background: BRAND.raised, border: `1px dashed ${BRAND.border}`,
          borderRadius: "8px", padding: "0.75rem 0.9rem",
        }}>
          No showcase images yet. Upload one below to start the rotation.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
          {items.map((it, i) => (
            <div key={it.id} style={{
              background: BRAND.raised, borderRadius: "8px", padding: "0.6rem",
              display: "flex", flexDirection: "column", gap: "0.4rem",
              border: `1px solid ${it.active ? "rgba(74,164,225,0.3)" : "rgba(255,255,255,0.08)"}`,
              opacity: it.active ? 1 : 0.55,
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "0.6rem" }}>
                <input
                  type="checkbox"
                  checked={!!it.active}
                  onChange={(e) => updateItem(i, { active: e.target.checked })}
                  style={{ width: "18px", height: "18px", flexShrink: 0, cursor: "pointer", accentColor: BRAND.sky, marginTop: "0.5rem" }}
                />
                <div style={{
                  width: "72px", height: "72px", borderRadius: "6px", flexShrink: 0,
                  overflow: "hidden", background: "#000",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginTop: "0.1rem",
                }}>
                  <img src={it.imageUrl} alt={it.headline || it.caption || ""} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.4rem", minWidth: 0 }}>
                  <input
                    type="text"
                    value={it.headline || ""}
                    onChange={(e) => updateItem(i, { headline: e.target.value })}
                    placeholder="Headline (big, bold) — e.g. 'POFFERTJES TODAY!'"
                    style={{
                      background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "6px",
                      color: "white", fontSize: "0.92rem", fontWeight: 700,
                      padding: "0.45rem 0.55rem", outline: "none",
                    }}
                  />
                  <textarea
                    value={it.caption || ""}
                    onChange={(e) => updateItem(i, { caption: e.target.value })}
                    placeholder="Body message (smaller) — a sentence or two of marketing copy beneath the headline. Example: &quot;Dutch mini-pancakes, gluten-free, dusted with powdered sugar — made fresh while you wait.&quot;"
                    rows={3}
                    style={{
                      background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "6px",
                      color: "white", fontSize: "0.82rem", lineHeight: 1.4,
                      padding: "0.45rem 0.55rem", outline: "none",
                      resize: "vertical", fontFamily: "inherit",
                    }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", flexShrink: 0 }}>
                  <button onClick={() => moveItem(i, -1)} disabled={i === 0} style={{ ...miniBtn, opacity: i === 0 ? 0.3 : 1 }}>↑</button>
                  <button onClick={() => moveItem(i, 1)} disabled={i === items.length - 1} style={{ ...miniBtn, opacity: i === items.length - 1 ? 0.3 : 1 }}>↓</button>
                  <button onClick={() => removeItem(i)} style={{ ...miniBtn, color: BRAND.pink }}>✕</button>
                </div>
              </div>
              <div style={{ marginLeft: "26px", display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <input
                  type="text"
                  value={it.category || ""}
                  onChange={(e) => updateItem(i, { category: e.target.value })}
                  placeholder="Category tag (optional)"
                  style={{
                    flex: 1,
                    background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "6px",
                    color: "rgba(255,255,255,0.65)", fontSize: "0.74rem",
                    padding: "0.3rem 0.5rem", outline: "none",
                  }}
                />
                <label style={{ color: BRAND.dim, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Duration
                </label>
                <select
                  value={it.durationSec ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateItem(i, { durationSec: v === "" ? null : Number(v) });
                  }}
                  title="How long this card stays on screen. Default = inherit rotation default."
                  style={{
                    background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "5px",
                    color: BRAND.sky, fontSize: "0.74rem", padding: "0.3rem 0.4rem", cursor: "pointer",
                  }}
                >
                  <option value="">Default ({rotation.intervalSec}s)</option>
                  {[6, 8, 10, 12, 15, 20, 30, 45, 60].map((s) => (
                    <option key={s} value={s}>{s}s</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: "0.5rem" }}>
        <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.gif" onChange={handleUpload} style={{ display: "none" }} />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          style={{ flex: 1, background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: BRAND.sky, fontSize: "0.82rem", padding: "0.55rem", cursor: uploading ? "wait" : "pointer", fontWeight: 600 }}
        >
          {uploading ? "Uploading…" : "+ Upload showcase image"}
        </button>
        <button
          onClick={save}
          disabled={saving}
          style={{ flex: 1, background: saved ? BRAND.green : BRAND.gold, color: saved ? "white" : BRAND.dark, border: "none", borderRadius: "8px", fontSize: "0.82rem", padding: "0.55rem", cursor: "pointer", fontWeight: "bold" }}
        >
          {saving ? "Saving…" : saved ? "✓ Saved!" : "Save changes"}
        </button>
      </div>

      <div style={{ height: "1px", background: BRAND.divider, margin: "0.3rem 0" }} />

      <div style={{ color: BRAND.dim, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "-0.2rem" }}>
        Rotation
      </div>
      <ToggleRow
        label="Weave 'As seen on' press card into rotation"
        checked={!!rotation.includePressCard}
        onChange={(v) => updateRotation("includePressCard", v)}
      />
      <ToggleRow
        label="Weave one pancake card into rotation (experimental — currently uses full-res cutouts; can cause memory buildup over long sessions)"
        checked={!!rotation.includeRecentPancakes}
        onChange={(v) => updateRotation("includeRecentPancakes", v)}
      />
      <Field label="Default duration (seconds)" hint="How long each card stays on screen before crossfading. Per-card overrides above take precedence.">
        <input
          type="number"
          min={3}
          max={120}
          value={rotation.intervalSec ?? 12}
          onChange={(e) => updateRotation("intervalSec", Math.max(3, Math.min(120, Number(e.target.value) || 12)))}
          style={inputStyle}
        />
      </Field>
      <Field label="Press card duration (seconds)" hint="How long the 'As seen on' card stays on screen. Leave blank to use the default.">
        <input
          type="number"
          min={3}
          max={120}
          value={rotation.pressDurationSec ?? ""}
          placeholder={`${rotation.intervalSec ?? 12} (default)`}
          onChange={(e) => {
            const v = e.target.value;
            updateRotation("pressDurationSec", v === "" ? null : Math.max(3, Math.min(120, Number(v) || 12)));
          }}
          style={inputStyle}
        />
      </Field>
      <Field label="Pancake card duration (seconds)" hint="How long each pancake card stays on screen. Description-heavy cards may want longer.">
        <input
          type="number"
          min={3}
          max={120}
          value={rotation.pancakeDurationSec ?? ""}
          placeholder={`${rotation.intervalSec ?? 12} (default)`}
          onChange={(e) => {
            const v = e.target.value;
            updateRotation("pancakeDurationSec", v === "" ? null : Math.max(3, Math.min(120, Number(v) || 12)));
          }}
          style={inputStyle}
        />
      </Field>
    </Section>
  );
}

// ── CUSTOM ALERT (free-form announcement) ───────────────────────────────────
function CustomAlertSection() {
  const [text, setText]       = useState("");
  const [character, setChar]  = useState("hermiethewurmy");
  const [firing, setFiring]   = useState(false);

  async function fire() {
    if (!text.trim()) return;
    setFiring(true);
    try {
      await writeAlert("custom_announcement", { message: text.trim() }, { character });
      setText("");
    } catch (err) {
      alert("Custom alert failed: " + err.message);
    }
    setTimeout(() => setFiring(false), 800);
  }

  return (
    <Section title="📢 Fire Custom Alert" subtitle="Free-form announcement that fires immediately to /overlay/alerts.">
      <Field label="Message">
        <input
          style={inputStyle}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Special order — give it up for Bree!"
        />
      </Field>
      <Field label="Character">
        <select style={inputStyle} value={character} onChange={(e) => setChar(e.target.value)}>
          {FEATURED_CHOICES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </Field>
      <button
        onClick={fire}
        disabled={!text.trim() || firing}
        style={{
          background: firing ? BRAND.green : BRAND.gold,
          color: firing ? "white" : BRAND.dark,
          border: "none", borderRadius: "10px",
          fontSize: "0.88rem", fontWeight: "bold",
          padding: "0.65rem", cursor: text.trim() ? "pointer" : "not-allowed",
          opacity: text.trim() ? 1 : 0.5,
        }}
      >
        {firing ? "✓ Fired" : "🔔 Fire Custom Alert"}
      </button>
    </Section>
  );
}

// ── WEBHOOK INTEGRATION ──────────────────────────────────────────────────────
function WebhookSection() {
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied]         = useState(null);
  const baseUrl = "https://thejoyofpancakes.com/api/triggerAlert";

  function copy(id, text) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(id); setTimeout(() => setCopied(null), 1800);
    });
  }

  return (
    <Collapsible
      title="🔗 Generic Alert Webhook"
      subtitle="POST to this URL from Streamer.bot, IFTTT, Stream Deck, curl, or any HTTP tool to fire an alert. Body: { secret, type, fields?, character?, message?, ttlSec? }"
    >
      <Field label="Webhook URL">
        <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
          <code style={{ flex: 1, fontSize: "0.75rem", color: BRAND.sky, wordBreak: "break-all", fontFamily: "monospace" }}>{baseUrl}</code>
          <button onClick={() => copy("url", baseUrl)} style={{ ...miniBtn, color: copied === "url" ? BRAND.green : BRAND.sky, fontSize: "0.78rem", padding: "0.35rem 0.6rem" }}>
            {copied === "url" ? "✓" : "Copy"}
          </button>
        </div>
      </Field>
      <Field label="Shared secret" hint="Set via Firebase: firebase functions:secrets:set ALERT_WEBHOOK_SECRET">
        <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
          <code style={{ flex: 1, fontSize: "0.75rem", color: showSecret ? "white" : BRAND.dim, fontFamily: "monospace", letterSpacing: showSecret ? 0 : "0.2em" }}>
            {showSecret ? "(stored in Firebase secrets — not readable from here)" : "••••••••••••"}
          </code>
          <button onClick={() => setShowSecret(s => !s)} style={{ ...miniBtn, color: BRAND.muted, fontSize: "0.78rem", padding: "0.35rem 0.6rem" }}>
            {showSecret ? "Hide" : "Reveal"}
          </button>
        </div>
      </Field>
      <div style={{ background: "rgba(74,164,225,0.08)", border: "1px solid rgba(74,164,225,0.25)", borderRadius: "8px", padding: "0.65rem 0.85rem", fontSize: "0.78rem", color: BRAND.sky, lineHeight: 1.6 }}>
        <strong>Example curl:</strong><br />
        <code style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.65)" }}>
          curl -X POST {baseUrl} -H "Content-Type: application/json" -d '{"{"}' "secret":"YOUR_SECRET", "type":"new_follow", "fields":{"{"}'"name":"Jamie"{"}"} {"}"}'
        </code>
      </div>
    </Collapsible>
  );
}

// ── TWITCH INTEGRATION ───────────────────────────────────────────────────────
function TwitchSection() {
  const [copied, setCopied] = useState(false);
  const webhookUrl = "https://thejoyofpancakes.com/api/twitchEventSub";

  return (
    <Collapsible
      title="📺 Twitch EventSub"
      subtitle="Connect your Twitch channel to fire alerts on follows, subs, cheers, gift subs, and raids."
    >
      <Field label="EventSub callback URL">
        <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
          <code style={{ flex: 1, fontSize: "0.75rem", color: BRAND.sky, wordBreak: "break-all", fontFamily: "monospace" }}>{webhookUrl}</code>
          <button onClick={() => { navigator.clipboard.writeText(webhookUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }} style={{ ...miniBtn, color: copied ? BRAND.green : BRAND.sky, fontSize: "0.78rem", padding: "0.35rem 0.6rem" }}>
            {copied ? "✓" : "Copy"}
          </button>
        </div>
      </Field>
      <div style={{ background: "rgba(74,164,225,0.08)", border: "1px solid rgba(74,164,225,0.25)", borderRadius: "8px", padding: "0.85rem", fontSize: "0.82rem", color: BRAND.sky, lineHeight: 1.7, display: "flex", flexDirection: "column", gap: "0.3rem" }}>
        <div style={{ fontWeight: "bold", marginBottom: "0.2rem" }}>Setup steps:</div>
        <div>1. Go to <strong>dev.twitch.tv</strong> → Your Console → Register Your Application</div>
        <div>2. Set <strong>OAuth Redirect URL</strong> to <code style={{ fontSize: "0.72rem" }}>http://localhost</code> (not used for EventSub, but required)</div>
        <div>3. Set <strong>Category</strong> to "Application Integration"</div>
        <div>4. Copy your <strong>Client ID</strong> and generate a <strong>Client Secret</strong></div>
        <div>5. Choose a webhook secret (any string, 10-100 chars) and save it:<br />
          <code style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.6)" }}>firebase functions:secrets:set TWITCH_WEBHOOK_SECRET</code>
        </div>
        <div>6. Get a Twitch app access token:<br />
          <code style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.6)" }}>curl -X POST "https://id.twitch.tv/oauth2/token?client_id=YOUR_ID&client_secret=YOUR_SECRET&grant_type=client_credentials"</code>
        </div>
        <div>7. Register EventSub subscriptions using the Twitch CLI or API. Example for follows:<br />
          <code style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.6)" }}>
            twitch api post eventsub/subscriptions -b '{"{"}'"type":"channel.follow","version":"2","condition":{"{"}'"broadcaster_user_id":"YOUR_CHANNEL_ID","moderator_user_id":"YOUR_CHANNEL_ID"{"}"},"transport":{"{"}'"method":"webhook","callback":"{webhookUrl}","secret":"YOUR_WEBHOOK_SECRET"{"}"}{"}"}'
          </code>
        </div>
        <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem", marginTop: "0.3rem" }}>
          Supported events: channel.follow, channel.subscribe, channel.subscription.gift, channel.cheer, channel.raid
        </div>
      </div>
    </Collapsible>
  );
}

// ── SQUARE WEBHOOK ───────────────────────────────────────────────────────────
function SquareWebhookSection() {
  const [copied, setCopied] = useState(false);
  const webhookUrl = "https://thejoyofpancakes.com/api/squareAlertWebhook";

  return (
    <Collapsible
      title="💳 Square Tip Alerts"
      subtitle="Automatically fires a tip_received alert (with the Mario coin sound!) when a Square payment includes a tip."
    >
      <Field label="Webhook URL">
        <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
          <code style={{ flex: 1, fontSize: "0.75rem", color: BRAND.sky, wordBreak: "break-all", fontFamily: "monospace" }}>{webhookUrl}</code>
          <button onClick={() => { navigator.clipboard.writeText(webhookUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }} style={{ ...miniBtn, color: copied ? BRAND.green : BRAND.sky, fontSize: "0.78rem", padding: "0.35rem 0.6rem" }}>
            {copied ? "✓" : "Copy"}
          </button>
        </div>
      </Field>
      <div style={{ background: "rgba(74,164,225,0.08)", border: "1px solid rgba(74,164,225,0.25)", borderRadius: "8px", padding: "0.85rem", fontSize: "0.82rem", color: BRAND.sky, lineHeight: 1.7, display: "flex", flexDirection: "column", gap: "0.3rem" }}>
        <div style={{ fontWeight: "bold", marginBottom: "0.2rem" }}>Setup steps:</div>
        <div>1. Go to <strong>developer.squareup.com</strong> → Your app → Webhooks</div>
        <div>2. Click <strong>Add Endpoint</strong>, paste the webhook URL above</div>
        <div>3. Subscribe to <strong>payment.completed</strong> events</div>
        <div>4. Copy the <strong>Signature Key</strong> Square generates and save it:<br />
          <code style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.6)" }}>firebase functions:secrets:set SQUARE_WEBHOOK_SIG_KEY</code>
        </div>
        <div>5. To enable tipping on Square checkouts, enable it in your Square Dashboard → Checkout settings</div>
        <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem", marginTop: "0.3rem" }}>
          Only payments with a non-zero tip fire an alert. Regular queue payments and preservation orders are silently skipped.
        </div>
      </div>
    </Collapsible>
  );
}
